import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, MailOpen, Mail, Trash2, RefreshCw, Inbox, Download, Plus } from "lucide-react";
import Seo from "@/components/Seo";
import { ASSETS, INQUIRY_TYPES } from "@/data/site";
import AdminEnergy from "@/components/admin/AdminEnergy";
import AdminChat from "@/components/admin/AdminChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("bnm_admin_token")}` });
const typeLabel = (v) => INQUIRY_TYPES.find((t) => t.value === v)?.label || v;

const STATUS_STYLES = {
  new: "bg-volt/15 text-volt border-volt/30",
  read: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  archived: "bg-slate-500/10 text-slate-500 border-slate-600/30",
  paid: "bg-volt/15 text-volt border-volt/30",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  failed: "bg-red-500/10 text-red-400 border-red-500/30",
  refunded: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const inputCls = "h-10 w-full rounded-md border border-white/15 bg-obsidian/60 px-3 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt";

function Inquiries() {
  const [inquiries, setInquiries] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/inquiries`, { headers: adminHeaders() });
    setInquiries(data.inquiries);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id + status);
    try {
      await axios.patch(`${API}/admin/inquiries/${id}`, { status }, { headers: adminHeaders() });
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } finally { setBusyId(null); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    setBusyId(id + "delete");
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: adminHeaders() });
      setInquiries((prev) => prev.filter((i) => i.id !== id));
    } finally { setBusyId(null); }
  };

  if (!inquiries) return <p className="py-10 text-sm text-slate-500">Loading…</p>;
  if (inquiries.length === 0) return (
    <div className="rounded-lg border border-dashed border-white/15 bg-pine/40 p-14 text-center">
      <Inbox className="h-10 w-10 mx-auto text-volt/60" aria-hidden="true" />
      <p className="mt-4 font-display text-lg font-bold text-white">No inquiries yet</p>
    </div>
  );
  return (
    <div className="space-y-4">
      {inquiries.map((inq) => (
        <article key={inq.id} data-testid={`admin-inquiry-${inq.id}`} className="rounded-lg border border-white/10 bg-pine/60 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display text-base font-bold text-white">{inq.name}</h3>
                <span className={`rounded-full border px-3 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] ${STATUS_STYLES[inq.status] || STATUS_STYLES.new}`}>{inq.status}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-volt/70">{typeLabel(inq.inquiry_type)}</span>
              </div>
              <p className="mt-1.5 text-xs text-slate-400">
                {inq.organization ? `${inq.organization} · ` : ""}
                <a href={`mailto:${inq.email}`} className="hover:text-volt transition-colors">{inq.email}</a>
                {inq.phone ? ` · ${inq.phone}` : ""}
              </p>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500">{new Date(inq.created_at).toLocaleString()}</p>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 whitespace-pre-wrap">{inq.message}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {inq.status !== "read" && (
              <button onClick={() => setStatus(inq.id, "read")} disabled={busyId} data-testid={`admin-mark-read-${inq.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3.5 py-2 text-xs font-medium text-slate-300 hover:text-volt hover:border-volt/40 transition-colors disabled:opacity-50">
                <MailOpen className="h-3.5 w-3.5" aria-hidden="true" /> Mark read
              </button>
            )}
            {inq.status !== "new" && (
              <button onClick={() => setStatus(inq.id, "new")} disabled={busyId} data-testid={`admin-mark-new-${inq.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3.5 py-2 text-xs font-medium text-slate-300 hover:text-volt hover:border-volt/40 transition-colors disabled:opacity-50">
                <Mail className="h-3.5 w-3.5" aria-hidden="true" /> Mark new
              </button>
            )}
            <button onClick={() => remove(inq.id)} disabled={busyId} data-testid={`admin-delete-${inq.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3.5 py-2 text-xs font-medium text-slate-300 hover:text-red-400 hover:border-red-400/40 transition-colors disabled:opacity-50">
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function SolarOrders() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/solar/overview`, { headers: adminHeaders() });
    setData(data);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const setOrderStatus = async (ref, payment_status) => {
    try {
      await axios.patch(`${API}/admin/solar/orders/${ref}`, { payment_status }, { headers: adminHeaders() });
      toast.success(`Order ${ref} marked ${payment_status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/solar/export`, { headers: adminHeaders(), responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bnm-solar-orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <p className="py-10 text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-10">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-white">Orders ({data.orders.length})</h2>
          <button onClick={exportCsv} data-testid="admin-export-csv" className="inline-flex items-center gap-2 rounded-md border border-white/15 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-volt hover:border-volt/40 transition-colors">
            <Download className="h-4 w-4" aria-hidden="true" /> Export CSV
          </button>
        </div>
        {data.orders.length === 0 && <p className="mt-4 text-sm text-slate-500">No orders yet.</p>}
        <div className="mt-4 space-y-3">
          {data.orders.map((o) => (
            <div key={o.reference} data-testid={`admin-order-${o.reference}`} className="rounded-lg border border-white/10 bg-pine/60 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-sm font-semibold text-white">{o.reference}</p>
                  <p className="mt-1 text-xs text-slate-400">{o.customer_email} · {o.currency.toUpperCase()} {o.amount} · {o.provider} · {new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${STATUS_STYLES[o.payment_status]}`}>{o.payment_status}</span>
                  {o.payment_status !== "paid" && (
                    <button onClick={() => setOrderStatus(o.reference, "paid")} data-testid={`admin-order-paid-${o.reference}`} className="rounded-md border border-volt/40 px-3 py-2 text-xs font-semibold text-volt hover:bg-volt/10 transition-colors">Mark paid</button>
                  )}
                  {o.payment_status === "paid" && (
                    <button onClick={() => setOrderStatus(o.reference, "refunded")} data-testid={`admin-order-refund-${o.reference}`} className="rounded-md border border-white/15 px-3 py-2 text-xs font-medium text-slate-300 hover:text-red-400 hover:border-red-400/40 transition-colors">Mark refunded</button>
                  )}
                  {o.payment_status === "pending" && (
                    <button onClick={() => setOrderStatus(o.reference, "failed")} className="rounded-md border border-white/15 px-3 py-2 text-xs font-medium text-slate-300 hover:text-red-400 hover:border-red-400/40 transition-colors">Mark failed</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-white">Customers ({data.customers.length})</h2>
        {data.customers.length === 0 && <p className="mt-4 text-sm text-slate-500">No customers yet.</p>}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.customers.map((c) => (
            <div key={c.id} className="rounded-lg border border-white/10 bg-pine/60 p-5">
              <p className="font-semibold text-white text-sm">{c.name}</p>
              <p className="mt-1 text-xs text-slate-400">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
              <p className="mt-1 text-xs text-slate-500">{[c.city, c.country].filter(Boolean).join(", ") || "—"} · {c.customer_type || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-white">Calculations ({data.calculations.length})</h2>
        <div className="mt-4 space-y-2">
          {data.calculations.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white/10 bg-pine/40 px-4 py-3">
              <p className="text-xs text-slate-300">{c.customer_email} · {c.profile?.customer_type || "—"} · {c.results?.free?.daily_kwh} kWh/day</p>
              <p className="font-mono text-[10px] text-slate-500">{new Date(c.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SolarSettings() {
  const [cfg, setCfg] = useState(null);
  const [appliances, setAppliances] = useState([]);
  const [newApp, setNewApp] = useState({ name: "", watts: 100, hours: 2, surge: 1, category: "Custom" });

  const load = useCallback(async () => {
    const [c, pub] = await Promise.all([
      axios.get(`${API}/admin/solar/config`, { headers: adminHeaders() }),
      axios.get(`${API}/solar/config`),
    ]);
    setCfg(c.data);
    setAppliances(pub.data.appliances);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!cfg) return <p className="py-10 text-sm text-slate-500">Loading…</p>;

  const setA = (k) => (e) => setCfg((c) => ({ ...c, assumptions: { ...c.assumptions, [k]: Number(e.target.value) } }));
  const setD = (k) => (e) => setCfg((c) => ({ ...c, default_distances_m: { ...c.default_distances_m, [k]: Number(e.target.value) } }));

  const save = async () => {
    try {
      const payload = {
        ...cfg,
        inverter_sizes_kw: typeof cfg.inverter_sizes_kw === "string"
          ? cfg.inverter_sizes_kw.split(",").map((s) => Number(s.trim())).filter((n) => n > 0)
          : cfg.inverter_sizes_kw,
      };
      await axios.put(`${API}/admin/solar/config`, payload, { headers: adminHeaders() });
      toast.success("Calculator settings saved");
    } catch {
      toast.error("Save failed — check values");
    }
  };

  const saveAppliance = async (a) => {
    try {
      await axios.put(`${API}/admin/solar/appliances/${a.key}`, a, { headers: adminHeaders() });
      toast.success(`${a.name} saved`);
    } catch { toast.error("Save failed"); }
  };
  const deleteAppliance = async (a) => {
    if (!window.confirm(`Remove ${a.name} from the library?`)) return;
    await axios.delete(`${API}/admin/solar/appliances/${a.key}`, { headers: adminHeaders() });
    setAppliances((prev) => prev.filter((x) => x.key !== a.key));
  };
  const addAppliance = async () => {
    if (!newApp.name.trim()) return;
    try {
      const key = "custom_" + newApp.name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40);
      await axios.post(`${API}/admin/solar/appliances`, { ...newApp, key }, { headers: adminHeaders() });
      toast.success("Appliance added");
      setNewApp({ name: "", watts: 100, hours: 2, surge: 1, category: "Custom" });
      load();
    } catch { toast.error("Add failed"); }
  };

  return (
    <div className="space-y-10">
      <div className="rounded-lg border border-white/10 bg-pine/60 p-6">
        <h2 className="font-display text-lg font-bold text-white">Pricing & Payment</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="block text-xs text-slate-400">Professional design price
            <input type="number" step="any" min={0} className={`${inputCls} mt-1.5`} data-testid="cfg-price" value={cfg.pricing.professional_amount}
              onChange={(e) => setCfg((c) => ({ ...c, pricing: { ...c.pricing, professional_amount: Number(e.target.value) } }))} />
          </label>
          <label className="block text-xs text-slate-400">Currency
            <input className={`${inputCls} mt-1.5`} value={cfg.pricing.currency}
              onChange={(e) => setCfg((c) => ({ ...c, pricing: { ...c.pricing, currency: e.target.value } }))} />
          </label>
          <label className="block text-xs text-slate-400">Payment provider
            <select className={`${inputCls} mt-1.5`} data-testid="cfg-provider" value={cfg.payment_provider}
              onChange={(e) => setCfg((c) => ({ ...c, payment_provider: e.target.value }))}>
              <option value="manual">Manual (Orange Money / bank transfer — BNM confirms)</option>
              <option value="stripe">Stripe online checkout (requires configured keys)</option>
            </select>
          </label>
        </div>
        <label className="mt-4 block text-xs text-slate-400">Manual payment instructions (shown to customers)
          <textarea rows={3} className="mt-1.5 w-full rounded-md border border-white/15 bg-obsidian/60 px-3 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
            data-testid="cfg-manual-instructions" value={cfg.manual_payment_instructions}
            onChange={(e) => setCfg((c) => ({ ...c, manual_payment_instructions: e.target.value }))} />
        </label>
      </div>

      <div className="rounded-lg border border-white/10 bg-pine/60 p-6">
        <h2 className="font-display text-lg font-bold text-white">Engineering Assumptions</h2>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(cfg.assumptions).map(([k, v]) => (
            <label key={k} className="block text-xs text-slate-400">{k.replace(/_/g, " ")}
              <input type="number" step="any" className={`${inputCls} mt-1.5`} data-testid={`cfg-assumption-${k}`} value={v} onChange={setA(k)} />
            </label>
          ))}
          <label className="block text-xs text-slate-400">Panel wattage (W)
            <input type="number" className={`${inputCls} mt-1.5`} data-testid="cfg-panel-wattage" value={cfg.panel_wattage}
              onChange={(e) => setCfg((c) => ({ ...c, panel_wattage: Number(e.target.value) }))} />
          </label>
          <label className="block text-xs text-slate-400">Inverter sizes (kW, comma-separated)
            <input className={`${inputCls} mt-1.5`} value={Array.isArray(cfg.inverter_sizes_kw) ? cfg.inverter_sizes_kw.join(", ") : cfg.inverter_sizes_kw}
              onChange={(e) => setCfg((c) => ({ ...c, inverter_sizes_kw: e.target.value }))} />
          </label>
          <label className="block text-xs text-slate-400">Default battery chemistry
            <select className={`${inputCls} mt-1.5`} value={cfg.default_chemistry}
              onChange={(e) => setCfg((c) => ({ ...c, default_chemistry: e.target.value }))}>
              <option value="lithium">Lithium / LiFePO4</option>
              <option value="leadacid">Lead-acid</option>
            </select>
          </label>
          {[["solar", "Default panels→inverter (m)"], ["battery", "Default battery→inverter (m)"], ["ac", "Default inverter→board (m)"]].map(([k, label]) => (
            <label key={k} className="block text-xs text-slate-400">{label}
              <input type="number" step="any" className={`${inputCls} mt-1.5`} value={cfg.default_distances_m[k]} onChange={setD(k)} />
            </label>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500">Battery options: {cfg.battery_options.map((b) => `${b.name} (${b.kwh} kWh, ${b.chemistry})`).join(" · ")}</p>
        <button onClick={save} data-testid="cfg-save" className="mt-6 inline-flex items-center rounded-md bg-volt px-6 py-3 text-sm font-semibold text-obsidian hover:bg-volt-soft hover:text-obsidian transition-colors">
          Save Calculator Settings
        </button>
      </div>

      <div className="rounded-lg border border-white/10 bg-pine/60 p-6">
        <h2 className="font-display text-lg font-bold text-white">Appliance Library ({appliances.length})</h2>
        <div className="mt-5 space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {appliances.map((a) => (
            <div key={a.key} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-center rounded-md border border-white/10 bg-obsidian/40 p-3">
              <p className="col-span-2 sm:col-span-2 text-sm font-medium text-white">{a.name}</p>
              {["watts", "hours", "surge"].map((f) => (
                <label key={f} className="block text-[10px] text-slate-500">{f}
                  <input type="number" step="any" className={`${inputCls} mt-0.5 h-9`} value={a[f]}
                    onChange={(e) => setAppliances((prev) => prev.map((x) => (x.key === a.key ? { ...x, [f]: Number(e.target.value) } : x)))} />
                </label>
              ))}
              <div className="col-span-2 sm:col-span-1 flex gap-1.5 justify-end">
                <button onClick={() => saveAppliance(a)} data-testid={`cfg-app-save-${a.key}`} className="rounded-md border border-volt/40 px-3 py-2 text-xs font-semibold text-volt hover:bg-volt/10">Save</button>
                <button onClick={() => deleteAppliance(a)} data-testid={`cfg-app-del-${a.key}`} className="rounded-md border border-white/15 px-3 py-2 text-xs text-slate-400 hover:text-red-400 hover:border-red-400/40">Del</button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end rounded-md border border-dashed border-white/15 p-3">
          <label className="col-span-2 block text-[10px] text-slate-500">Name
            <input className={`${inputCls} mt-0.5 h-9`} data-testid="cfg-app-new-name" value={newApp.name} onChange={(e) => setNewApp((n) => ({ ...n, name: e.target.value }))} />
          </label>
          <label className="block text-[10px] text-slate-500">Watts
            <input type="number" className={`${inputCls} mt-0.5 h-9`} value={newApp.watts} onChange={(e) => setNewApp((n) => ({ ...n, watts: Number(e.target.value) }))} />
          </label>
          <label className="block text-[10px] text-slate-500">Hours/day
            <input type="number" step="any" className={`${inputCls} mt-0.5 h-9`} value={newApp.hours} onChange={(e) => setNewApp((n) => ({ ...n, hours: Number(e.target.value) }))} />
          </label>
          <label className="block text-[10px] text-slate-500">Surge ×
            <input type="number" step="any" className={`${inputCls} mt-0.5 h-9`} value={newApp.surge} onChange={(e) => setNewApp((n) => ({ ...n, surge: Number(e.target.value) }))} />
          </label>
          <button onClick={addAppliance} data-testid="cfg-app-add" className="inline-flex items-center justify-center gap-1.5 rounded-md border border-volt/40 px-3 py-2 text-xs font-semibold text-volt hover:bg-volt/10">
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add
          </button>
        </div>
      </div>
    </div>
  );
}

const TABS = [["inquiries", "Inquiries"], ["solar", "Solar Orders & Customers"], ["energy", "Energy Intelligence"], ["chat", "AI Assistant"], ["settings", "Calculator Settings"]];

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("bnm_admin_token")) { navigate("/admin/login"); return; }
    axios.get(`${API}/auth/me`, { headers: adminHeaders() })
      .then((r) => {
        if (r.data.role !== "admin") { navigate("/admin/login"); return; }
        setReady(true);
      })
      .catch(() => { localStorage.removeItem("bnm_admin_token"); navigate("/admin/login"); });
  }, [navigate]);

  const logout = () => { localStorage.removeItem("bnm_admin_token"); navigate("/admin/login"); };

  if (!ready) return null;

  return (
    <>
      <Seo title="Admin Dashboard — BNM Sustainable Energy Limited" description="Restricted area." />
      <section data-testid="admin-dashboard" className="relative min-h-screen bg-obsidian grid-lines-dark noise-overlay">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={ASSETS.logo} alt="" className="h-10 w-10 rounded-md ring-1 ring-white/15" />
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-white">BNM Admin</h1>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-volt/70">Inquiries · Solar service · Settings</p>
              </div>
            </div>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-md border border-white/15 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-red-400 hover:border-red-400/40 transition-colors">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Log out
            </button>
          </div>

          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Admin sections">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                data-testid={`admin-tab-${key}`}
                onClick={() => setTab(key)}
                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                  tab === key ? "border-volt bg-volt/15 text-volt" : "border-white/15 text-slate-400 hover:text-white hover:border-white/30"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "inquiries" && <Inquiries />}
            {tab === "solar" && <SolarOrders />}
            {tab === "energy" && <AdminEnergy />}
            {tab === "chat" && <AdminChat />}
            {tab === "settings" && <SolarSettings />}
          </div>

          <p className="mt-10 text-center">
            <Link to="/" className="text-xs text-slate-500 hover:text-volt transition-colors">← Back to website</Link>
          </p>
        </div>
      </section>
    </>
  );
}
